import { AccommodationRatePickerProps } from "@/components/AccommodationRatePicker/AccommodationRatePickerProps.ts";
import { useCheckAccommodationAvailibility } from "@/components/Inputs/RatesPicker/hooks/useCheckAccommodationAvailibility.ts";
import { useCheckPaymentConfigCompatibility } from "@/components/Inputs/RatesPicker/hooks/useCheckPaymentConfigCompatibility.ts";
import { RateRow } from "@/components/RateRow/RateRow.tsx";
import { calculateNights } from "@/helpers/calculateNights.ts";
import { clearReservationOfferURLParam } from "@/helpers/clearReservationOfferURLParam.ts";
import { handleGTMAddAccommodationToCart } from "@/helpers/gtm/handleGTMAddAccommodationToCart.ts";
import { RatePlanMerged } from "@/helpers/interfaces/RatePlanMerged.ts";
import { mergeRatePlans } from "@/helpers/mergeRatePlans.ts";
import { useScrollToTop } from "@/helpers/scrollToTop.ts";
import { sortRatePlansByPriority } from "@/helpers/sortRatePlansByPriority.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { BedDouble, ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

const DEFAULT_VISIBLE_COUNT = 3;

export const AccommodationRatePicker = ({
  rateGroup,
  onSelectRatePlan,
  initialVisibleCount = DEFAULT_VISIBLE_COUNT,
  showFullDescription = false,
}: AccommodationRatePickerProps) => {
  const { t } = useTranslation();
  const { addCartItem, setPage, defaultDateRange, cartAccommodationItems } = useEngineStore(
    useShallow((state) => ({
      addCartItem: state.addCartItem,
      setPage: state.setPage,
      defaultDateRange: state.defaultDateRange,
      cartAccommodationItems: state.cartAccommodationItems,
    })),
  );
  const scrollToTop = useScrollToTop();
  const isRGUnavailable = useCheckAccommodationAvailibility(rateGroup);
  const paymentCompatibilityMap = useCheckPaymentConfigCompatibility(rateGroup);
  const datesNotSelected = !defaultDateRange?.[0] || !defaultDateRange?.[1];
  const isDisabled = datesNotSelected || isRGUnavailable;
  const [isExpanded, setIsExpanded] = useState(false);

  const reservationNights = useMemo(() => {
    if (!defaultDateRange?.[0] || !defaultDateRange?.[1]) return 0;
    return calculateNights(defaultDateRange[0], defaultDateRange[1]);
  }, [defaultDateRange]);

  const availableRatePlans = useMemo(() => {
    const merged = mergeRatePlans(rateGroup);
    return sortRatePlansByPriority(merged.filter((rp) => rp.isAvailable));
  }, [rateGroup]);

  const cartRatePlanIds = useMemo(() => {
    const ids = cartAccommodationItems
      .filter((item) => item.rateGroup.id === rateGroup.id)
      .map((item) => item.ratePlan.id);
    return new Set(ids);
  }, [cartAccommodationItems, rateGroup.id]);

  const hasMore = availableRatePlans.length > initialVisibleCount;
  const hiddenCount = availableRatePlans.length - initialVisibleCount;
  const visibleRatePlans = isExpanded ? availableRatePlans : availableRatePlans.slice(0, initialVisibleCount);

  const handleSelectRatePlan = (rp: RatePlanMerged) => {
    if (isDisabled) return;
    addCartItem({ ...rp, rateGroup });
    handleGTMAddAccommodationToCart(rp, rateGroup);
    clearReservationOfferURLParam();
    onSelectRatePlan?.();
    setPage(AppPage.ExtraOptions);
    scrollToTop();
  };

  const handleToggleShowMore = () => {
    setIsExpanded((v) => !v);
  };

  if (availableRatePlans.length === 0) return null;

  return (
    <div
      className={`c-accommodation-rate-picker${
        isRGUnavailable ? " c-accommodation-rate-picker--unavailable" : ""
      }`}
    >
      {isRGUnavailable && (
        <div className="c-accommodation-rate-picker__unavailable-overlay">
          <BedDouble size={20} strokeWidth={1.5} />
          <span>{t("noUnitsLeft")}</span>
        </div>
      )}
      <div className="c-accommodation-rate-picker__rows">
        {visibleRatePlans.map((rp, index) => (
          <RateRow
            key={rp.id}
            ratePlan={rp}
            isSelected={false}
            reservationNights={reservationNights}
            onSelect={() => handleSelectRatePlan(rp)}
            disabled={isDisabled}
            showRadio={false}
            isLast={!hasMore && index === visibleRatePlans.length - 1}
            isInCart={cartRatePlanIds.has(rp.id)}
            isPaymentIncompatible={paymentCompatibilityMap[rp.id] === false}
            mode="picker"
            showFullDescription={showFullDescription}
          />
        ))}
      </div>
      {hasMore && (
        <button
          type="button"
          className={`c-accommodation-rate-picker__show-more${isExpanded ? " is-expanded" : ""}`}
          onClick={handleToggleShowMore}
        >
          <span>{isExpanded ? t("showFewerRates") : t("showMoreRates", { count: hiddenCount })}</span>
          <ChevronDown size={16} />
        </button>
      )}
    </div>
  );
};
